import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControlLabel,
  FormGroup
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon
} from "@mui/icons-material";
import Swal from "sweetalert2";
import Header from "../../../components/Header";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { reportsManagementUrl, roleManagemenUrl } from "../../../config";

const PermissionManagementPage = ({ roleId, role, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken?.Id);
  const companyId = parseInt(decodedToken?.CompanyId);

  const [majorPermissions, setMajorPermissions] = useState([]);
  const [minorPermissions, setMinorPermissions] = useState([]);
  const [reportTypePermissions, setReportTypePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMajor, setSelectedMajor] = useState({});
  const [selectedMinor, setSelectedMinor] = useState({});
  const [selectedReportTypes, setSelectedReportTypes] = useState({});
  
  // Select all toggles
  const [selectAllMajor, setSelectAllMajor] = useState(false);
  const [selectAllMinor, setSelectAllMinor] = useState({});
  const [selectAllReport, setSelectAllReport] = useState({});
  
  // Grouped data
  const [minorsByMajor, setMinorsByMajor] = useState({});
  const [reportTypesByMinor, setReportTypesByMinor] = useState({});

  const base_url = reportsManagementUrl.uri;
  const base_url2 = roleManagemenUrl.uri;
  const steps = ['Major Classifications', 'Minor Classifications', 'Report Types'];

  // Fetch all permission data
  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        
        // Fetch major permissions
        const majorResponse = await fetchJsonDynamic(
          "reportsMajorClassifications",
          null,
          null,
          base_url
        );
        if (majorResponse?.results) {
          const majorData = JSON.parse(majorResponse.results);
          setMajorPermissions(majorData || []);
        }

        // Fetch minor permissions
        const minorResponse = await fetchJsonDynamic(
          "reportsMinorClassifications",
          null,
          null,
          base_url
        );
        if (minorResponse?.results) {
          const minorData = JSON.parse(minorResponse.results);
          setMinorPermissions(minorData || []);
        }

        // Fetch report type permissions
        const reportTypesResponse = await fetchJsonDynamic(
          "reportTypes",
          null,
          null,
          base_url
        );
        if (reportTypesResponse?.results) {
          const reportTypesData = JSON.parse(reportTypesResponse.results);
          setReportTypePermissions(reportTypesData || []);
        }

        // Fetch existing permissions for this role
        await fetchExistingPermissions();

      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load permissions data.",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, [roleId]);

  // Group minors by major classification
  useEffect(() => {
    if (minorPermissions.length > 0 && majorPermissions.length > 0) {
      const grouped = {};
      
      majorPermissions.forEach(major => {
        const majorId = getItemId(major);
        grouped[majorId] = {
          major: major,
          minors: minorPermissions.filter(minor => 
            getMajorIdFromMinor(minor) === majorId
          )
        };
      });
      
      setMinorsByMajor(grouped);
    }
  }, [minorPermissions, majorPermissions]);

  // Group report types by minor classification
  useEffect(() => {
    if (reportTypePermissions.length > 0 && minorPermissions.length > 0) {
      const grouped = {};
      
      minorPermissions.forEach(minor => {
        const minorId = getItemId(minor);
        grouped[minorId] = {
          minor: minor,
          reportTypes: reportTypePermissions.filter(report => 
            getMinorIdFromReport(report) === minorId
          )
        };
      });
      
      setReportTypesByMinor(grouped);
    }
  }, [reportTypePermissions, minorPermissions]);

  // Update select all toggles when selections change
  useEffect(() => {
    // Update major select all toggle
    const allMajorSelected = majorPermissions.length > 0 && 
      majorPermissions.every(major => selectedMajor[getItemId(major)]);
    setSelectAllMajor(allMajorSelected);
    
    // Update minor select all toggles
    const minorSelectAllStates = {};
    Object.entries(minorsByMajor).forEach(([majorId, {minors}]) => {
      if (minors.length > 0) {
        minorSelectAllStates[majorId] = minors.every(minor => selectedMinor[getItemId(minor)]);
      }
    });
    setSelectAllMinor(minorSelectAllStates);
    
    // Update report select all toggles
    const reportSelectAllStates = {};
    Object.entries(reportTypesByMinor).forEach(([minorId, {reportTypes}]) => {
      if (reportTypes.length > 0) {
        reportSelectAllStates[minorId] = reportTypes.every(report => selectedReportTypes[getItemId(report)]);
      }
    });
    setSelectAllReport(reportSelectAllStates);
  }, [selectedMajor, selectedMinor, selectedReportTypes, majorPermissions, minorsByMajor, reportTypesByMinor]);

  // When a major is deselected, deselect all its minors
  useEffect(() => {
    const updatedMinorSelections = { ...selectedMinor };
    let changed = false;

    Object.entries(minorsByMajor).forEach(([majorId, { minors }]) => {
      const isMajorSelected = selectedMajor[majorId];
      
      if (!isMajorSelected) {
        minors.forEach(minor => {
          const minorId = getItemId(minor);
          if (selectedMinor[minorId]) {
            updatedMinorSelections[minorId] = false;
            changed = true;
          }
        });
      }
    });

    if (changed) {
      setSelectedMinor(updatedMinorSelections);
    }
  }, [selectedMajor, minorsByMajor]);

  // When a minor is deselected, deselect all its report types
  useEffect(() => {
    const updatedReportSelections = { ...selectedReportTypes };
    let changed = false;

    Object.entries(reportTypesByMinor).forEach(([minorId, { reportTypes }]) => {
      const isMinorSelected = selectedMinor[minorId];
      
      if (!isMinorSelected) {
        reportTypes.forEach(report => {
          const reportId = getItemId(report);
          if (selectedReportTypes[reportId]) {
            updatedReportSelections[reportId] = false;
            changed = true;
          }
        });
      }
    });

    if (changed) {
      setSelectedReportTypes(updatedReportSelections);
    }
  }, [selectedMinor, reportTypesByMinor]);

  // Fetch existing permissions for the role
  const fetchExistingPermissions = async () => {
    try {
      // Get token and remove any surrounding quotes
      let token = localStorage.getItem('token');
      if (token && typeof token === 'string') {
        token = token.replace(/^"(.*)"$/, '$1');
        token = token.replace(/^'(.*)'$/, '$1');
      }

      // Initialize selections
      const initialMajor = {};
      const initialMinor = {};
      const initialReportTypes = {};

      // Fetch major permissions
      const majorQuery = `
        query {
          majorReportsPermissionsByRole(roleId: ${roleId}) {
            majorId
            isActive
          }
        }
      `;

      const majorResponse = await fetch(base_url2 , {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: majorQuery })
      });

      const majorResult = await majorResponse.json();
      if (majorResponse.ok && majorResult.data?.majorReportsPermissionsByRole) {
        majorResult.data.majorReportsPermissionsByRole.forEach(perm => {
          initialMajor[perm.majorId] = Boolean(perm.isActive);
        });
      }

      // Fetch minor permissions
      const minorQuery = `
        query {
          minorReportsPermissionsbyRole(roleId: ${roleId}) {
            minorId
            isActive
          }
        }
      `;

      const minorResponse = await fetch(base_url2 , {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: minorQuery })
      });

      const minorResult = await minorResponse.json();
      if (minorResponse.ok && minorResult.data?.minorReportsPermissionsbyRole) {
        minorResult.data.minorReportsPermissionsbyRole.forEach(perm => {
          initialMinor[perm.minorId] = Boolean(perm.isActive);
        });
      }

      // Fetch report type permissions
      const reportQuery = `
        query {
          reportTypesPermissionsbyRole(roleId: ${roleId}) {
            typeId
            isActive
          }
        }
      `;

      const reportResponse = await fetch(base_url2 , {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: reportQuery })
      });

      const reportResult = await reportResponse.json();
      if (reportResponse.ok && reportResult.data?.reportTypesPermissionsbyRole) {
        reportResult.data.reportTypesPermissionsbyRole.forEach(perm => {
          initialReportTypes[perm.typeId] = Boolean(perm.isActive);
        });
      }

      // Set default false for any permissions not returned from the API
      majorPermissions.forEach(major => {
        const majorId = getItemId(major);
        if (initialMajor[majorId] === undefined) {
          initialMajor[majorId] = false;
        }
      });
      
      minorPermissions.forEach(minor => {
        const minorId = getItemId(minor);
        if (initialMinor[minorId] === undefined) {
          initialMinor[minorId] = false;
        }
      });
      
      reportTypePermissions.forEach(report => {
        const reportId = getItemId(report);
        if (initialReportTypes[reportId] === undefined) {
          initialReportTypes[reportId] = false;
        }
      });

      setSelectedMajor(initialMajor);
      setSelectedMinor(initialMinor);
      setSelectedReportTypes(initialReportTypes);

    } catch (error) {
      console.error("Error fetching existing permissions:", error);
      // Initialize with all false values as fallback
      const majorInitial = {};
      majorPermissions.forEach(major => {
        majorInitial[getItemId(major)] = false;
      });
      
      const minorInitial = {};
      minorPermissions.forEach(minor => {
        minorInitial[getItemId(minor)] = false;
      });
      
      const reportInitial = {};
      reportTypePermissions.forEach(report => {
        reportInitial[getItemId(report)] = false;
      });

      setSelectedMajor(majorInitial);
      setSelectedMinor(minorInitial);
      setSelectedReportTypes(reportInitial);
    }
  };

  const handlePermissionChange = (type, id, isAllowed) => {
    switch (type) {
      case 'major':
        setSelectedMajor(prev => ({
          ...prev,
          [id]: isAllowed
        }));
        break;
      case 'minor':
        setSelectedMinor(prev => ({
          ...prev,
          [id]: isAllowed
        }));
        break;
      case 'report':
        setSelectedReportTypes(prev => ({
          ...prev,
          [id]: isAllowed
        }));
        break;
      default:
        break;
    }
  };

  const handleSelectAll = (type, parentId = null, select = true) => {
    switch (type) {
      case 'major':
        const allMajorSelections = {};
        majorPermissions.forEach(major => {
          allMajorSelections[getItemId(major)] = select;
        });
        setSelectedMajor(allMajorSelections);
        break;
      
      case 'minor':
        const minorSelections = { ...selectedMinor };
        if (parentId) {
          // Select all minors for a specific major
          minorsByMajor[parentId]?.minors.forEach(minor => {
            minorSelections[getItemId(minor)] = select;
          });
        } else {
          // Select all minors across all majors
          minorPermissions.forEach(minor => {
            minorSelections[getItemId(minor)] = select;
          });
        }
        setSelectedMinor(minorSelections);
        break;
      
      case 'report':
        const reportSelections = { ...selectedReportTypes };
        if (parentId) {
          // Select all reports for a specific minor
          reportTypesByMinor[parentId]?.reportTypes.forEach(report => {
            reportSelections[getItemId(report)] = select;
          });
        } else {
          // Select all reports across all minors
          reportTypePermissions.forEach(report => {
            reportSelections[getItemId(report)] = select;
          });
        }
        setSelectedReportTypes(reportSelections);
        break;
      
      default:
        break;
    }
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Get token and remove any surrounding quotes
      let token = localStorage.getItem('token');
      
      // Remove surrounding quotes if they exist
      if (token && typeof token === 'string') {
        token = token.replace(/^"(.*)"$/, '$1');
        token = token.replace(/^'(.*)'$/, '$1');
      }
      
      // Check if token exists and is properly formatted
      if (!token || token.split('.').length !== 3) {
        throw new Error('Invalid or missing authentication token');
      }

      // Prepare the input data
      const majorPermissionsInput = Object.entries(selectedMajor).map(([typeId, isActive]) => ({
        typeId: parseInt(typeId),
        isActive: isActive ? 1 : 0
      }));

      const minorPermissionsInput = Object.entries(selectedMinor).map(([typeId, isActive]) => ({
        typeId: parseInt(typeId),
        isActive: isActive ? 1 : 0
      }));

      const reportTypePermissionsInput = Object.entries(selectedReportTypes).map(([typeId, isActive]) => ({
        typeId: parseInt(typeId),
        isActive: isActive ? 1 : 0
      }));

      // Construct the GraphQL mutation
      const mutation = `
        mutation UpsertPermissions(
          $roleId: Int!
          $majorPermissions: [PermissionInput!]!
          $minorPermissions: [PermissionInput!]!
          $reportTypePermissions: [PermissionInput!]!
        ) {
          upsertPermissions(
            roleId: $roleId
            majorPermissions: $majorPermissions
            minorPermissions: $minorPermissions
            reportTypePermissions: $reportTypePermissions
          ) {
            error
            results
            totalCount
          }
        }
      `;

      // Execute the GraphQL request
      const response = await fetch(base_url2, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: mutation,
          variables: {
            roleId: roleId,
            majorPermissions: majorPermissionsInput,
            minorPermissions: minorPermissionsInput,
            reportTypePermissions: reportTypePermissionsInput
          }
        })
      });

      const result = await response.json();

      if (response.ok) {
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
        
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Permissions updated successfully!",
        });
        if (onClose) onClose();
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please contact the Admin.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getItemId = (item) => item.Id || item.id;
  const getMajorName = (major) => major.MajorClassification || major.name || `Major ${getItemId(major)}`;
  const getMinorName = (minor) => minor.MinorClassification || minor.name || `Minor ${getItemId(minor)}`;
  const getReportName = (report) => report.ReportName || report.name || `Report ${getItemId(report)}`;

  const getMajorIdFromMinor = (minor) => minor.MajorClassificationId || minor.MajorClassificationID;
  const getMinorIdFromReport = (report) => report.MinorClassificationId || report.MinorClassificationID;

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                  Major Classifications Permissions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Allow or deny access to major report classifications
                </Typography>
              </Box>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectAllMajor}
                      onChange={(e) => handleSelectAll('major', null, e.target.checked)}
                      color="primary"
                    />
                  }
                  label={selectAllMajor ? "Deselect All" : "Select All"}
                />
              </FormGroup>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {majorPermissions.map((major) => {
                const majorId = getItemId(major);
                return (
                  <Grid item xs={12} md={6} key={majorId}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[300]}`,
                      height: '100%',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                      }
                    }}>
                      <Typography variant="body1" fontWeight="medium">
                        {getMajorName(major)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ mr: 1, color: selectedMajor[majorId] ? 'success.main' : 'text.secondary', fontWeight: 'medium' }}>
                          {selectedMajor[majorId] ? 'Allowed' : 'Not Allowed'}
                        </Typography>
                        <Switch
                          checked={Boolean(selectedMajor[majorId])}
                          onChange={(e) => handlePermissionChange('major', majorId, e.target.checked)}
                          color="success"
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        );
      
      case 1:
        return (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                  Minor Classifications Permissions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Allow or deny access to minor report classifications (only showing minors for selected majors)
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {Object.entries(minorsByMajor).map(([majorId, {major, minors}]) => {
              const isMajorSelected = selectedMajor[getItemId(major)];
              const selectedMinorsCount = minors.filter(minor => selectedMinor[getItemId(minor)]).length;
              
              // Don't show major sections that have no minors or are not selected
              if (minors.length === 0 || !isMajorSelected) return null;
              
              return (
                <Accordion 
                  key={majorId} 
                  defaultExpanded={true}
                  sx={{ mb: 2, border: `1px solid ${theme.palette.grey[300]}` }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }} fontWeight="bold">
                        {getMajorName(major)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={`${selectedMinorsCount}/${minors.length}`} 
                          size="small" 
                          color={selectAllMinor[majorId] ? "success" : selectedMinorsCount > 0 ? "primary" : "default"}
                          variant={selectedMinorsCount > 0 && !selectAllMinor[majorId] ? "outlined" : "filled"}
                        />
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={selectAllMinor[majorId] || false}
                                onChange={(e) => handleSelectAll('minor', majorId, e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={selectAllMinor[majorId] ? "Deselect All" : "Select All"}
                          />
                        </FormGroup>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {minors.map((minor) => {
                        const minorId = getItemId(minor);
                        return (
                          <Grid item xs={12} md={6} key={minorId}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between', 
                              p: 2,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.grey[300]}`,
                              height: '100%',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'grey.50',
                              }
                            }}>
                              <Typography variant="body1">
                                {getMinorName(minor)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ mr: 1, color: selectedMinor[minorId] ? 'success.main' : 'text.secondary', fontWeight: 'medium' }}>
                                  {selectedMinor[minorId] ? 'Allowed' : 'Not Allowed'}
                                </Typography>
                                <Switch
                                  checked={Boolean(selectedMinor[minorId])}
                                  onChange={(e) => handlePermissionChange('minor', minorId, e.target.checked)}
                                  color="success"
                                />
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        );
      
      case 2:
        return (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                  Report Types Permissions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Allow or deny access to specific report types (only showing reports for selected minors)
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {Object.entries(reportTypesByMinor).map(([minorId, {minor, reportTypes}]) => {
              const majorId = getMajorIdFromMinor(minor);
              const isMajorSelected = selectedMajor[majorId];
              const isMinorSelected = selectedMinor[getItemId(minor)];
              const selectedReportsCount = reportTypes.filter(report => selectedReportTypes[getItemId(report)]).length;
              
              // Don't show minor sections that have no report types or where parent is not selected
              if (reportTypes.length === 0 || !isMajorSelected || !isMinorSelected) return null;
              
              return (
                <Accordion 
                  key={minorId} 
                  defaultExpanded={true}
                  sx={{ mb: 2, border: `1px solid ${theme.palette.grey[300]}` }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {getMinorName(minor)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" fontWeight="medium">
                          Major: {majorPermissions.find(m => getItemId(m) === majorId)?.MajorClassification || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={`${selectedReportsCount}/${reportTypes.length}`} 
                          size="small" 
                          color={selectAllReport[minorId] ? "success" : selectedReportsCount > 0 ? "primary" : "default"}
                          variant={selectedReportsCount > 0 && !selectAllReport[minorId] ? "outlined" : "filled"}
                        />
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={selectAllReport[minorId] || false}
                                onChange={(e) => handleSelectAll('report', minorId, e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={selectAllReport[minorId] ? "Deselect All" : "Select All"}
                          />
                        </FormGroup>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {reportTypes.map((report) => {
                        const reportId = getItemId(report);
                        return (
                          <Grid item xs={12} md={6} key={reportId}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between', 
                              p: 2,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.grey[300]}`,
                              height: '100%',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'grey.50',
                              }
                            }}>
                              <Typography variant="body1">
                                {getReportName(report)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ mr: 1, color: selectedReportTypes[reportId] ? 'success.main' : 'text.secondary', fontWeight: 'medium' }}>
                                  {selectedReportTypes[reportId] ? 'Allowed' : 'Not Allowed'}
                                </Typography>
                                <Switch
                                  checked={Boolean(selectedReportTypes[reportId])}
                                  onChange={(e) => handlePermissionChange('report', reportId, e.target.checked)}
                                  color="success"
                                />
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        );
      
      default:
        return null;
    }
  };

  const getActiveCount = (type) => {
    const items = type === 'major' ? selectedMajor : type === 'minor' ? selectedMinor : selectedReportTypes;
    return Object.values(items).filter(isActive => isActive).length;
  };

  const getTotalCount = (type) => {
    return type === 'major' ? majorPermissions.length : 
           type === 'minor' ? minorPermissions.length : 
           reportTypePermissions.length;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Header
        title="Manage Permissions"
        subtitle={`Assign permissions for ${role}`}
      />
      
      <Card>
        <CardContent>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>
                  {label} ({getActiveCount(['major', 'minor', 'report'][index])}/{getTotalCount(['major', 'minor', 'report'][index])})
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step content */}
          {renderStepContent(activeStep)}

          {/* Navigation buttons */}
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={activeStep === 0 ? onClose : handleBack}
              disabled={submitting}
              startIcon={activeStep === 0 ? null : <ArrowBackIcon />}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>

            <Box>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={submitting}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? "Saving..." : "Save Permissions"}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PermissionManagementPage;